import gql from "graphql-tag";

import { Fragment, Mad } from "../types";

import { TemplateFragment, TEMPLATE_FRAGMENT } from "./Template";

import {
  UserMinimalFragment,
  UserSummaryFragment,
  USER_MINIMAL_FRAGMENT,
  USER_SUMMARY_FRAGMENT,
} from "./User";

import { DocumentFragment, DOCUMENT_FRAGMENT } from "./Document";

import {
  CampaignMinimalFragment,
  CAMPAIGN_MINIMAL_FRAGMENT,
} from "./Campaign";

import {
  MadSupervisorFragment,
  MAD_SUPERVISOR_FRAGMENT
} from "./MadSupervisor";

import { ContractMinimalFragment, CONTRACT_MINIMAL_FRAGMENT } from "./Contract";

import {
  MadReportFragment,
  MadReportFullFragment,
  MAD_REPORT_FRAGMENT,
  MAD_REPORT_FULL_FRAGMENT,
} from "./MadReport";

export const MAD_FRAGMENT = gql`
  fragment MadFragment on Mad {
    id
    createdAt
    updatedAt
    startDate
    endDate
    nextReportAt
    madFunction
    missionDescription
    title
    canAddDocument
    canDeleteDocument
    canInsert
    referentIds
    schedule
    template {
      ...TemplateFragment
    }
    documents {
      ...DocumentFragment
    }
    talent {
      ...UserSummaryFragment
    }
    referents {
      ...UserSummaryFragment
    }
    campaign {
      ...CampaignMinimalFragment
    }
    madSupervisors {
      ...MadSupervisorFragment
    }
    contracts {
      ...ContractMinimalFragment
    }
    lastCheckedReport {
      ...MadReportFragment
    }
    reports {
      ...MadReportFragment
    }
  }

  ${TEMPLATE_FRAGMENT}
  ${DOCUMENT_FRAGMENT}
  ${USER_SUMMARY_FRAGMENT}
  ${CAMPAIGN_MINIMAL_FRAGMENT}
  ${CONTRACT_MINIMAL_FRAGMENT}
  ${MAD_REPORT_FRAGMENT}
  ${MAD_SUPERVISOR_FRAGMENT}
`;

// prettier-ignore
export type MadFragment = Fragment<
  Mad,
  Pick<Mad, "id" | "createdAt" | "updatedAt" | "startDate" | "endDate" | "nextReportAt" | "madFunction" | "title" | "missionDescription" | "referentIds" | "canAddDocument" | "canDeleteDocument" | "canInsert">
    & {
      template: TemplateFragment,
      documents: DocumentFragment[],
      talent: UserSummaryFragment,
      referents: UserSummaryFragment[],
      campaign: CampaignMinimalFragment,
      contracts: ContractMinimalFragment[],
      lastCheckedReport: MadReportFragment,
      reports: MadReportFragment[],
      madSupervisors: MadSupervisorFragment[]
    }
>;

export const MAD_FULL_FRAGMENT = gql`
  fragment MadFullFragment on Mad {
    id
    createdAt
    updatedAt
    startDate
    endDate
    nextReportAt
    madFunction
    title
    missionDescription
    schedule
    canAddDocument
    canDeleteDocument
    canInsert
    referentIds
    template {
      ...TemplateFragment
    }
    documents {
      ...DocumentFragment
    }
    talent {
      ...UserSummaryFragment
    }
    referents {
      ...UserSummaryFragment
    }
    campaign {
      ...CampaignMinimalFragment
    }
    madSupervisors {
      ...MadSupervisorFragment
    }
    contracts {
      ...ContractMinimalFragment
    }
    lastCheckedReport {
      ...MadReportFragment
    }
    reports {
      ...MadReportFullFragment
    }
  }

  ${TEMPLATE_FRAGMENT}
  ${DOCUMENT_FRAGMENT}
  ${USER_SUMMARY_FRAGMENT}
  ${CAMPAIGN_MINIMAL_FRAGMENT}
  ${CONTRACT_MINIMAL_FRAGMENT}
  ${MAD_REPORT_FRAGMENT}
  ${MAD_REPORT_FULL_FRAGMENT}
  ${MAD_SUPERVISOR_FRAGMENT}
`;

// prettier-ignore
export type MadFullFragment = Fragment<
  Mad,
  Pick<Mad, "id" | "createdAt" | "updatedAt" | "startDate" | "endDate" | "nextReportAt" | "madFunction" | "title" | "missionDescription" | "canAddDocument" | "canDeleteDocument" | "canInsert" | "referentIds">
    & {
      schedule: string,
      template: TemplateFragment,
      documents: DocumentFragment[],
      talent: UserSummaryFragment,
      referents: UserSummaryFragment[],
      campaign: CampaignMinimalFragment,
      contracts: ContractMinimalFragment[],
      lastCheckedReport: MadReportFragment,
      reports: MadReportFullFragment[],
      madSupervisors: MadSupervisorFragment[]
    }
>;

export const MAD_MINIMAL_FRAGMENT = gql`
  fragment MadMinimalFragment on Mad {
    id
    title
    talent {
      ...UserMinimalFragment
    }
    referents {
      ...UserMinimalFragment
    }
    campaign {
      ...CampaignMinimalFragment
    }
    madSupervisors {
      ...MadSupervisorFragment
    }
  }

  ${USER_MINIMAL_FRAGMENT}
  ${CAMPAIGN_MINIMAL_FRAGMENT}
  ${MAD_SUPERVISOR_FRAGMENT}
`;

// prettier-ignore
export type MadMinimalFragment = Fragment<
  Mad,
  Pick<Mad, "id" | "title">
    & {
      talent: UserMinimalFragment;
      referents: UserMinimalFragment[];
      campaign: CampaignMinimalFragment;
      madSupervisors: MadSupervisorFragment[]
    }
>;
